<template>
    <div class="search-panel" :class="{'search-panel--filter-bar': aiEntityDetectionEnabled}" data-testid="search-panel">
        <SearchTabs v-if="!aiEntityDetectionEnabled" :value="activeTab">
            <SearchTab
                v-for="(tab, i) in tabs"
                :key="i"
                :class="tab.classes"
                :value="tab.value"
                :label="tab.label"
                :icon="tab.icon"
                :active="activeTab === tab.value"
                @click="onTabClick(tab)"
            />
        </SearchTabs>
        <SearchTabPanel :detached="aiEntityDetectionEnabled">
            <VJoyFilterBar v-if="aiEntityDetectionEnabled" class="search-panel__filter-bar">
                <VJoyFilterBarButton
                    v-for="tab in tabs"
                    :key="tab.value"
                    :value="tab.value"
                    :label="tab.label"
                    :active="activeTab === tab.value"
                    :data-testid="`search-panel-tab--${tab.value}`"
                    :class="{
                        'search-panel__filter-bar-button--ai': tab.value === 'ai',
                        'search-panel__filter-bar-button--ai--selected': tab.value === 'ai' && activeTab === tab.value,
                    }"
                    @click="onTabClick(tab)"
                />
            </VJoyFilterBar>
            <SearchForm
                v-if="activeTab === 'keywords'"
                :category-placeholder="`${props.searchFieldCategory}`"
                :location-placeholder="`${props.searchFieldLocation}`"
                :location-remote-country-label="props.searchLocationRemoteCountryLabel"
                :submit-text="t('index.btn.search')"
                :submit-link="localizedSearchSubmitLink"
                :brief-origin="`${props.briefOrigin}`"
                :is-browsing-global-matching="isBrowsingGlobalMatching"
            />
            <template v-if="activeTab === 'ai'">
                <AdvancedSearchPromptContainer
                    v-model="promptTerm"
                    shadow-focus
                    :bottom-aligned-actions="aiEntityDetectionEnabled"
                    :hide-brief-details-helper="aiEntityDetectionEnabled"
                    :hide-prompt-feedback="aiEntityDetectionEnabled"
                    :loading="isSubmittingPrompt"
                    @submit="onSubmitPrompt"
                />
                <PromptEntityIndicatorContainer v-if="aiEntityDetectionEnabled" :prompt="promptTerm" />
            </template>
        </SearchTabPanel>
    </div>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useDataTracking, useGlobalMatching, useLocalePrefix, useTranslation, useAuth} from '#imports';
    import {safelyNavigateTo} from '@malt/utils';
    import {VJoyFilterBar, VJoyFilterBarButton} from '@maltjoy/core-vue';
    import AdvancedSearchPromptContainer from './prompt/AdvancedSearchPromptContainer.vue';
    import PromptEntityIndicatorContainer from './prompt/PromptEntityIndicatorContainer.vue';
    import SearchForm from './SearchForm.vue';
    import SearchTab from './SearchTab.vue';
    import SearchTabPanel from './SearchTabPanel.vue';
    import SearchTabs from './SearchTabs.vue';
    import {useAdvancedSearch, type AdvancedSearchPromptOrigin} from '../composables/useAdvancedSearch';

    interface TabConfig {
        value: string;
        label: string;
        icon?: string;
        classes?: string;
    }

    const props = withDefaults(
        defineProps<{
            searchTab?: string | null;
            searchFieldCategory?: string | null;
            searchFieldLocation?: string | null;
            searchLocationRemoteCountryLabel?: string | null;
            searchSubmit?: string | null;
            searchSubmitLink?: string | null;
            aiSearchTab?: string | null;
            aiSearchTabLink?: string | null;
            briefOrigin?: string | null;
            pageSource?: string | null;
            origin?: AdvancedSearchPromptOrigin;
            aiEntityDetectionEnabled?: boolean;
        }>(),
        {
            aiEntityDetectionEnabled: false,
        },
    );

    const {submitAdvancedSearchPrompt, isSubmittingPrompt} = useAdvancedSearch();

    const authStore = useAuth();
    const {loggedIn} = storeToRefs(authStore);

    const {isBrowsingGlobalMatching} = useGlobalMatching();

    const {t} = useTranslation();
    const {track} = useDataTracking();
    const {withLocalePrefix} = useLocalePrefix();
    const activeTab = ref('keywords');
    const aiSearchUrl = ref('');
    const promptTerm = ref<string>('');

    const localizedSearchSubmitLink = computed(() => withLocalePrefix(props.searchSubmitLink || '/s'));

    const tabs = computed<TabConfig[]>(() => {
        const result: TabConfig[] = [
            {
                value: 'keywords',
                label: props.searchTab || '',
                icon: 'search',
            },
        ];

        if (props.aiSearchTab && props.aiSearchTabLink) {
            result.push({
                value: 'ai',
                label: props.aiSearchTab,
            });
        }

        return result;
    });

    if (props.aiSearchTab && props.aiSearchTabLink) {
        aiSearchUrl.value = props.aiSearchTabLink;
        // TODO : remove condition after Prismic clean up
        if (!aiSearchUrl.value.includes('brief_origin')) {
            aiSearchUrl.value +=
                (aiSearchUrl.value.includes('?') ? '&' : '?') + `brief_origin=${props.briefOrigin}&pageSource=home_unlogged_try_ai_search`;
        }
    }

    function trackTabClicked(tab: typeof SearchTab.props) {
        const labels: Record<string, string> = {
            ai: 'AI Search',
            keywords: 'Search',
        };

        track('temp_cms_homepage_tab_clicked', {cta_clicked: labels[tab.value]});
    }

    function onTabClick(tab: typeof SearchTab.props) {
        trackTabClicked(tab);

        const shouldRedirect = tab.value === 'ai' && loggedIn.value;

        if (shouldRedirect) {
            safelyNavigateTo(withLocalePrefix('/s/advanced'), {externalToThisApp: true});
            return;
        }

        activeTab.value = tab.value;
    }

    function onSubmitPrompt() {
        if (!promptTerm.value) return;
        submitAdvancedSearchPrompt(promptTerm.value, {
            briefOrigin: props.briefOrigin ?? undefined,
            pageSource: props.pageSource ?? undefined,
            origin: props.origin,
        });
    }
</script>

<style lang="scss" scoped>
    .search-panel {
        &__filter-bar {
            margin-bottom: var(--joy-core-spacing-4);
        }

        &__filter-bar-button {
            &--ai {
                &--selected,
                &:hover,
                &:focus {
                    border-color: var(--joy-color-ai-primary-30);
                }

                &--selected,
                &:focus {
                    background-color: var(--joy-color-ai-primary-10);
                }
            }
        }
    }
</style>
